import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUploadSection = ({ documents, onUpload, onRemove }) => {
  const [draggedOver, setDraggedOver] = useState(null);

  const documentTypes = [
    {
      id: 'cr-license',
      label: 'CR / Trade License',
      description: 'Commercial Registration or Trade License',
      required: true,
      validationChecks: [
        { id: 1, label: 'Document is clear and readable', status: documents?.['cr-license']?.file ? 'passed' : 'pending' },
        { id: 2, label: 'Registration number matches Tax ID', status: documents?.['cr-license']?.file ? 'passed' : 'pending' },
        { id: 3, label: 'Document is not expired', status: documents?.['cr-license']?.file ? 'passed' : 'pending' },
        { id: 4, label: 'Legal name matches registration', status: documents?.['cr-license']?.file ? 'warning' : 'pending' }
      ]
    },
    {
      id: 'vat-certificate',
      label: 'VAT Certificate',
      description: 'Value Added Tax Registration Certificate',
      required: true,
      validationChecks: [
        { id: 1, label: 'VAT number is valid format', status: documents?.['vat-certificate']?.file ? 'passed' : 'pending' },
        { id: 2, label: 'Certificate is current', status: documents?.['vat-certificate']?.file ? 'passed' : 'pending' },
        { id: 3, label: 'Company details match profile', status: documents?.['vat-certificate']?.file ? 'passed' : 'pending' }
      ]
    },
    {
      id: 'bank-letter',
      label: 'Bank Letter',
      description: 'Bank account verification letter',
      required: true,
      validationChecks: [
        { id: 1, label: 'Letter is on bank letterhead', status: documents?.['bank-letter']?.file ? 'passed' : 'pending' },
        { id: 2, label: 'Account holder name matches', status: documents?.['bank-letter']?.file ? 'passed' : 'pending' },
        { id: 3, label: 'Letter is dated within 3 months', status: documents?.['bank-letter']?.file ? 'passed' : 'pending' }
      ]
    }
  ];

  const handleFileSelect = (docType, event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      onUpload(docType, file);
    }
  };

  const handleDragOver = (docType, event) => {
    event?.preventDefault();
    setDraggedOver(docType);
  };

  const handleDragLeave = () => {
    setDraggedOver(null);
  };

  const handleDrop = (docType, event) => {
    event?.preventDefault();
    setDraggedOver(null);
    const file = event?.dataTransfer?.files?.[0];
    if (file) {
      onUpload(docType, file);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return 'CheckCircle2';
      case 'warning': return 'AlertTriangle';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'text-success';
      case 'warning': return 'text-warning';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="space-y-24">
      {documentTypes?.map((docType) => (
        <div key={docType?.id} className="bg-card border border-border rounded-lg p-16 md:p-24">
          <div className="flex items-start justify-between mb-16">
            <div>
              <div className="flex items-center gap-8 mb-4">
                <h3 className="text-base md:text-lg font-semibold text-foreground font-heading">
                  {docType?.label}
                </h3>
                {docType?.required && (
                  <span className="text-xs font-medium text-error caption">Required</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{docType?.description}</p>
            </div>
          </div>

          {!documents?.[docType?.id]?.file ? (
            <div
              onDragOver={(e) => handleDragOver(docType?.id, e)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(docType?.id, e)}
              className={`
                border-2 border-dashed rounded-lg p-24 md:p-32
                text-center transition-smooth cursor-pointer
                ${draggedOver === docType?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }
              `}
            >
              <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-12" />
              <p className="text-sm font-medium text-foreground mb-4">
                Drop file here or click to upload
              </p>
              <p className="text-xs text-muted-foreground caption mb-16">
                PDF, JPG, PNG up to 10MB
              </p>
              <input
                type="file"
                id={`file-${docType?.id}`}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileSelect(docType?.id, e)}
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                iconSize={16}
                onClick={() => document.getElementById(`file-${docType?.id}`)?.click()}
              >
                Choose File
              </Button>
            </div>
          ) : (
            <div className="space-y-16">
              <div className="flex items-center justify-between p-12 bg-muted rounded-lg">
                <div className="flex items-center gap-12 min-w-0 flex-1">
                  <Icon name="FileText" size={20} className="text-primary flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">
                      {documents?.[docType?.id]?.file?.name}
                    </p>
                    <p className="text-xs text-muted-foreground caption">
                      {(documents?.[docType?.id]?.file?.size / 1024)?.toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="X"
                  iconSize={16}
                  onClick={() => onRemove(docType?.id)}
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-16">
                <div className="flex items-center gap-8 mb-12">
                  <Icon name="Sparkles" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">AI Validation Results</span>
                </div>
                <div className="space-y-8">
                  {docType?.validationChecks?.map((check) => (
                    <div key={check?.id} className="flex items-start gap-8">
                      <Icon
                        name={getStatusIcon(check?.status)}
                        size={16}
                        className={`${getStatusColor(check?.status)} flex-shrink-0 mt-2`}
                      />
                      <span className="text-sm text-foreground">{check?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DocumentUploadSection;